import React, { useState, useEffect, useCallback } from 'react';
import { Scales, BookOpen, Receipt, Wallet, Bank, Coins, TrendUp, TrendDown, Plus, Trash, Check, FileText, Pencil, ChartBar, Money, Eye, ArrowLeft, MagnifyingGlass, Warning, Scroll } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import CashBookModule from './CashBookModule';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const fmtN = (v) => v ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
const fmtD = (v) => v ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
const TYPE_COLORS = { Asset: '#1565C0', Liability: '#C62828', Equity: '#2E7D32', Revenue: '#006D3B', Expense: '#E65100' };
const PIE_COLORS = ['#1565C0', '#2E7D32', '#E65100', '#7C4DFF', '#C62828', '#F57F17', '#00838F', '#AD1457'];

export default function AccountingModule() {
  const [tab, setTab] = useState('dashboard');
  // Dashboard
  const [dashboard, setDashboard] = useState(null);
  // Chart of Accounts
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState({ types: [], subtypes: {} });
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ code: '', name: '', type: 'Expense', subtype: '', description: '' });
  const [editAccount, setEditAccount] = useState(null);
  const [accountFilter, setAccountFilter] = useState('All');
  // Journal Entries
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);
  const [showAddJE, setShowAddJE] = useState(false);
  const [jeForm, setJeForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', reference: '', category: 'General', lines: [{ account_code: '', debit: '', credit: '', memo: '' }, { account_code: '', debit: '', credit: '', memo: '' }] });
  const [jeDetail, setJeDetail] = useState(null);
  // Quick Entry
  const [showQuickExpense, setShowQuickExpense] = useState(false);
  const [showQuickRevenue, setShowQuickRevenue] = useState(false);
  const [quickForm, setQuickForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', amount: '', expense_account: '', payment_account: '1000', revenue_account: '', deposit_account: '1000', reference: '', auto_post: true });
  // Reports
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  // General Ledger
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);

  const authFetch = async (url) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token');
    const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!r.ok) throw new Error(`${r.status}`);
    return r.json();
  };
  const authPost = async (url, body) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token');
    const r = await fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `${r.status}`); }
    return r.json();
  };
  const authPut = async (url, body) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token');
    const r = await fetch(url, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `${r.status}`); }
    return r.json();
  };
  const authDelete = async (url) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token');
    const r = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `${r.status}`); }
    return r.json();
  };

  const loadDashboard = useCallback(async () => { try { setDashboard(await authFetch(`${API}/accounting/dashboard`)); } catch (e) { console.error('Dashboard:', e); } }, []);
  const loadAccounts = useCallback(async () => { try { setAccounts(await authFetch(`${API}/accounting/chart-of-accounts`)); } catch (e) { console.error('Accounts:', e); } }, []);
  const loadAccountTypes = useCallback(async () => { try { setAccountTypes(await authFetch(`${API}/accounting/account-types`)); } catch (e) { console.error('Types:', e); } }, []);
  const loadJournalEntries = useCallback(async () => { try { const d = await authFetch(`${API}/accounting/journal-entries`); setJournalEntries(d.entries || []); setJournalTotal(d.total || 0); } catch (e) { console.error('JE:', e); } }, []);
  const loadIncomeStatement = useCallback(async () => { try { setIncomeStatement(await authFetch(`${API}/accounting/income-statement`)); } catch (e) { console.error('IS:', e); } }, []);
  const loadBalanceSheet = useCallback(async () => { try { setBalanceSheet(await authFetch(`${API}/accounting/balance-sheet`)); } catch (e) { console.error('BS:', e); } }, []);
  const loadCashFlow = useCallback(async () => { try { setCashFlow(await authFetch(`${API}/accounting/cash-flow`)); } catch (e) { console.error('CF:', e); } }, []);
  const loadTrialBalance = useCallback(async () => { try { setTrialBalance(await authFetch(`${API}/accounting/trial-balance`)); } catch (e) { console.error('TB:', e); } }, []);
  const loadLedger = useCallback(async (code) => { try { setLedgerData(await authFetch(`${API}/accounting/general-ledger/${code}`)); } catch (e) { console.error('GL:', e); } }, []);

  useEffect(() => { loadDashboard(); loadAccounts(); loadAccountTypes(); }, []);

  // Handlers
  const handleCreateAccount = async () => {
    try {
      await authPost(`${API}/accounting/chart-of-accounts`, accountForm);
      toast.success('Account created');
      setShowAddAccount(false);
      setAccountForm({ code: '', name: '', type: 'Expense', subtype: '', description: '' });
      loadAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleUpdateAccount = async () => {
    try {
      await authPut(`${API}/accounting/chart-of-accounts/${editAccount.code}`, editAccount);
      toast.success('Account updated');
      setEditAccount(null);
      loadAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteAccount = async (code) => {
    if (!window.confirm('Delete this account?')) return;
    try {
      await authDelete(`${API}/accounting/chart-of-accounts/${code}`);
      toast.success('Account deleted');
      loadAccounts();
    } catch (e) { toast.error(e.message); }
  };

  const handleCreateJE = async () => {
    try {
      const lines = jeForm.lines.filter(l => l.account_code && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0)).map(l => ({
        account_code: l.account_code,
        account_name: accounts.find(a => a.code === l.account_code)?.name || '',
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0,
        memo: l.memo || ''
      }));
      await authPost(`${API}/accounting/journal-entries`, { ...jeForm, lines });
      toast.success('Journal entry created');
      setShowAddJE(false);
      setJeForm({ date: new Date().toISOString().split('T')[0], description: '', reference: '', category: 'General', lines: [{ account_code: '', debit: '', credit: '', memo: '' }, { account_code: '', debit: '', credit: '', memo: '' }] });
      loadJournalEntries();
      loadDashboard();
    } catch (e) { toast.error(e.message); }
  };

  const handlePostJE = async (num) => {
    try {
      await authPost(`${API}/accounting/journal-entries/${num}/post`, {});
      toast.success('Journal entry posted');
      loadJournalEntries();
      loadAccounts();
      loadDashboard();
    } catch (e) { toast.error(e.message); }
  };

  const handleVoidJE = async (num) => {
    if (!window.confirm('Void this journal entry? This will reverse all balance changes.')) return;
    try {
      await authPost(`${API}/accounting/journal-entries/${num}/void`, {});
      toast.success('Journal entry voided');
      loadJournalEntries();
      loadAccounts();
      loadDashboard();
    } catch (e) { toast.error(e.message); }
  };

  const handleQuickExpense = async () => {
    try {
      await authPost(`${API}/accounting/quick-expense`, quickForm);
      toast.success('Expense recorded');
      setShowQuickExpense(false);
      setQuickForm({ ...quickForm, description: '', amount: '', expense_account: '', reference: '' });
      loadJournalEntries();
      loadAccounts();
      loadDashboard();
    } catch (e) { toast.error(e.message); }
  };

  const handleQuickRevenue = async () => {
    try {
      await authPost(`${API}/accounting/quick-revenue`, quickForm);
      toast.success('Revenue recorded');
      setShowQuickRevenue(false);
      setQuickForm({ ...quickForm, description: '', amount: '', revenue_account: '', reference: '' });
      loadJournalEntries();
      loadAccounts();
      loadDashboard();
    } catch (e) { toast.error(e.message); }
  };

  const addJELine = () => setJeForm({ ...jeForm, lines: [...jeForm.lines, { account_code: '', debit: '', credit: '', memo: '' }] });
  const removeJELine = (i) => { if (jeForm.lines.length <= 2) return; setJeForm({ ...jeForm, lines: jeForm.lines.filter((_, idx) => idx !== i) }); };
  const updateJELine = (i, field, val) => { const lines = [...jeForm.lines]; lines[i] = { ...lines[i], [field]: val }; setJeForm({ ...jeForm, lines }); };

  const filteredAccounts = accountFilter === 'All' ? accounts : accounts.filter(a => a.type === accountFilter);
  const expenseAccounts = accounts.filter(a => a.type === 'Expense' && a.is_active);
  const revenueAccounts = accounts.filter(a => a.type === 'Revenue' && a.is_active);
  const cashAccounts = accounts.filter(a => ['Cash', 'Bank'].includes(a.subtype) && a.is_active);

  const jeDebitTotal = jeForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const jeCreditTotal = jeForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const jeBalanced = Math.abs(jeDebitTotal - jeCreditTotal) < 0.01;

  // Stat card component
  const StatCard = ({ icon: Icon, label, value, color, sub }) => (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <span className="text-[11px] text-gray-500 font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-900">{value}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        if (v === 'journal') loadJournalEntries();
        if (v === 'income-statement') loadIncomeStatement();
        if (v === 'balance-sheet') loadBalanceSheet();
        if (v === 'cash-flow') loadCashFlow();
        if (v === 'trial-balance') loadTrialBalance();
        if (v === 'accounts') { loadAccounts(); loadAccountTypes(); }
        if (v === 'dashboard') loadDashboard();
      }}>
        <TabsList className="flex flex-wrap gap-1 bg-gray-50 p-1 rounded-xl">
          <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-[#1565C0] data-[state=active]:text-white text-xs"><ChartBar size={14} className="mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="accounts" className="rounded-lg data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white text-xs"><BookOpen size={14} className="mr-1" />Chart of Accounts</TabsTrigger>
          <TabsTrigger value="journal" className="rounded-lg data-[state=active]:bg-[#7C4DFF] data-[state=active]:text-white text-xs"><Scroll size={14} className="mr-1" />Journal Entries</TabsTrigger>
          <TabsTrigger value="income-statement" className="rounded-lg data-[state=active]:bg-[#006D3B] data-[state=active]:text-white text-xs"><TrendUp size={14} className="mr-1" />Income Statement</TabsTrigger>
          <TabsTrigger value="balance-sheet" className="rounded-lg data-[state=active]:bg-[#E65100] data-[state=active]:text-white text-xs"><Scales size={14} className="mr-1" />Balance Sheet</TabsTrigger>
          <TabsTrigger value="cash-flow" className="rounded-lg data-[state=active]:bg-[#00838F] data-[state=active]:text-white text-xs"><Coins size={14} className="mr-1" />Cash Flow</TabsTrigger>
          <TabsTrigger value="trial-balance" className="rounded-lg data-[state=active]:bg-[#C62828] data-[state=active]:text-white text-xs"><Scales size={14} className="mr-1" />Trial Balance</TabsTrigger>
        
          <TabsTrigger value="cash-book" className="rounded-lg data-[state=active]:bg-[#1565C0] data-[state=active]:text-white text-xs"><BookOpen size={14} className="mr-1" />Cash Book</TabsTrigger>
        </TabsList>

        {/* ===== DASHBOARD ===== */}
        <TabsContent value="dashboard" className="space-y-4 mt-4">
          {dashboard ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <StatCard icon={Bank} label="Total Assets" value={`${fmtN(dashboard.total_assets)} K`} color="#1565C0" />
                <StatCard icon={Warning} label="Total Liabilities" value={`${fmtN(dashboard.total_liabilities)} K`} color="#C62828" />
                <StatCard icon={Scales} label="Total Equity" value={`${fmtN(dashboard.total_equity)} K`} color="#2E7D32" />
                <StatCard icon={TrendUp} label="Total Revenue" value={`${fmtN(dashboard.total_revenue)} K`} color="#006D3B" />
                <StatCard icon={TrendDown} label="Total Expenses" value={`${fmtN(dashboard.total_expenses)} K`} color="#E65100" />
                <StatCard icon={Money} label="Net Income" value={`${fmtN(dashboard.net_income)} K`} color={dashboard.net_income >= 0 ? '#006D3B' : '#C62828'} sub={`Margin: ${dashboard.profit_margin}%`} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Wallet} label="Cash Balance" value={`${fmtN(dashboard.cash_balance)} K`} color="#1565C0" />
                <StatCard icon={Receipt} label="Accounts Receivable" value={`${fmtN(dashboard.ar_balance)} K`} color="#F57F17" />
                <StatCard icon={Receipt} label="Accounts Payable" value={`${fmtN(dashboard.ap_balance)} K`} color="#C62828" />
                <StatCard icon={FileText} label="Journal Entries" value={`${dashboard.entry_stats?.total || 0}`} color="#7C4DFF" sub={`${dashboard.entry_stats?.draft || 0} draft, ${dashboard.entry_stats?.posted || 0} posted`} />
              </div>
              {/* Quick Actions */}
              <div className="flex gap-2">
                <button onClick={() => setShowQuickExpense(true)} className="flex items-center gap-1.5 bg-[#E65100] text-white text-xs py-2 px-4 rounded-lg hover:bg-[#BF360C]"><TrendDown size={14} weight="bold" />Record Expense</button>
                <button onClick={() => setShowQuickRevenue(true)} className="flex items-center gap-1.5 bg-[#006D3B] text-white text-xs py-2 px-4 rounded-lg hover:bg-[#004D40]"><TrendUp size={14} weight="bold" />Record Revenue</button>
                <button onClick={() => { setTab('journal'); loadJournalEntries(); }} className="flex items-center gap-1.5 bg-[#7C4DFF] text-white text-xs py-2 px-4 rounded-lg hover:bg-[#651FFF]"><Scroll size={14} weight="bold" />New Journal Entry</button>
              </div>
              {/* Monthly Trend Chart */}
              {dashboard.monthly_trend?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Monthly Revenue vs Expenses (Last 6 Months)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dashboard.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtN(v)} />
                      <Tooltip formatter={(v) => fmtN(v)} />
                      <Bar dataKey="revenue" fill="#006D3B" name="Revenue" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expense" fill="#E65100" name="Expenses" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Expense Breakdown */}
                {Object.keys(dashboard.expense_breakdown || {}).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Expense Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={Object.entries(dashboard.expense_breakdown).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                          {Object.keys(dashboard.expense_breakdown).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => fmtN(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Revenue Breakdown */}
                {Object.keys(dashboard.revenue_breakdown || {}).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Revenue Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={Object.entries(dashboard.revenue_breakdown).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                          {Object.keys(dashboard.revenue_breakdown).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => fmtN(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              {/* Recent Entries */}
              {dashboard.recent_entries?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Journal Entries</h3>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-gray-100"><th className="text-left py-2 text-gray-500">Date</th><th className="text-left py-2 text-gray-500">Entry #</th><th className="text-left py-2 text-gray-500">Description</th><th className="text-right py-2 text-gray-500">Amount</th><th className="text-center py-2 text-gray-500">Status</th></tr></thead>
                    <tbody>
                      {dashboard.recent_entries.map((e, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2">{fmtDate(e.date)}</td>
                          <td className="py-2 font-mono text-[#7C4DFF]">{e.entry_number}</td>
                          <td className="py-2">{e.description}</td>
                          <td className="py-2 text-right font-medium">{fmtN(e.total_debit)}</td>
                          <td className="py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${e.status === 'Posted' ? 'bg-green-100 text-green-700' : e.status === 'Voided' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{e.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Balance Check */}
              <div className={`rounded-xl p-3 text-xs font-medium flex items-center gap-2 ${dashboard.is_balanced ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {dashboard.is_balanced ? <Check size={16} weight="bold" /> : <Warning size={16} weight="bold" />}
                {dashboard.is_balanced ? 'Accounting equation balanced: Assets = Liabilities + Equity' : 'Warning: Accounting equation is NOT balanced!'}
              </div>
            </>
          ) : <div className="text-center py-12 text-gray-400">Loading dashboard...</div>}
        </TabsContent>

        {/* ===== CHART OF ACCOUNTS ===== */}
        <TabsContent value="accounts" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5 flex-wrap">
              {['All', ...ACCOUNT_TYPES_LIST].map(t => (
                <button key={t} onClick={() => setAccountFilter(t)} className={`text-[11px] px-3 py-1.5 rounded-lg font-medium transition-colors ${accountFilter === t ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={accountFilter === t ? { backgroundColor: t === 'All' ? '#1565C0' : TYPE_COLORS[t] || '#1565C0' } : {}}>{t}</button>
              ))}
            </div>
            <button onClick={() => setShowAddAccount(true)} className="flex items-center gap-1 bg-[#1565C0] text-white text-xs py-2 px-3 rounded-lg hover:bg-[#0D47A1]"><Plus size={14} weight="bold" />Add Account</button>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b border-gray-100"><th className="text-left py-2.5 px-3 text-gray-500 font-medium">Code</th><th className="text-left py-2.5 px-3 text-gray-500 font-medium">Account Name</th><th className="text-left py-2.5 px-3 text-gray-500 font-medium">Type</th><th className="text-left py-2.5 px-3 text-gray-500 font-medium">Subtype</th><th className="text-right py-2.5 px-3 text-gray-500 font-medium">Balance</th><th className="text-center py-2.5 px-3 text-gray-500 font-medium">Status</th><th className="text-center py-2.5 px-3 text-gray-500 font-medium">Actions</th></tr></thead>
              <tbody>
                {filteredAccounts.map((a, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                    <td className="py-2 px-3 font-mono font-medium text-gray-700">{a.code}</td>
                    <td className="py-2 px-3 font-medium text-gray-800">{a.name}</td>
                    <td className="py-2 px-3"><span className="text-[10px] px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: TYPE_COLORS[a.type] }}>{a.type}</span></td>
                    <td className="py-2 px-3 text-gray-500">{a.subtype}</td>
                    <td className={`py-2 px-3 text-right font-medium ${a.balance > 0 ? 'text-green-700' : a.balance < 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmtD(a.balance)}</td>
                    <td className="py-2 px-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setLedgerAccount(a.code); loadLedger(a.code); setTab('ledger'); }} className="text-[#1565C0] hover:bg-blue-50 p-1 rounded" title="View Ledger"><Eye size={14} /></button>
                        <button onClick={() => setEditAccount({ ...a })} className="text-[#F57F17] hover:bg-yellow-50 p-1 rounded" title="Edit"><Pencil size={14} /></button>
                        {!a.is_system && <button onClick={() => handleDeleteAccount(a.code)} className="text-[#C62828] hover:bg-red-50 p-1 rounded" title="Delete"><Trash size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAccounts.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No accounts found</div>}
          </div>
          {/* Account summary by type */}
          <div className="grid grid-cols-5 gap-3">
            {['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'].map(t => {
              const total = accounts.filter(a => a.type === t).reduce((s, a) => s + (a.balance || 0), 0);
              const count = accounts.filter(a => a.type === t).length;
              return (
                <div key={t} className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm text-center">
                  <div className="text-[10px] font-medium text-gray-500">{t} ({count})</div>
                  <div className="text-sm font-bold mt-1" style={{ color: TYPE_COLORS[t] }}>{fmtN(total)}</div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* ===== JOURNAL ENTRIES ===== */}
        <TabsContent value="journal" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Scroll size={16} weight="bold" className="text-[#7C4DFF]" />Journal Entries ({journalTotal})</h3>
            <div className="flex gap-2">
              <button onClick={() => setShowQuickExpense(true)} className="flex items-center gap-1 bg-[#E65100] text-white text-xs py-2 px-3 rounded-lg hover:bg-[#BF360C]"><TrendDown size={14} />Quick Expense</button>
              <button onClick={() => setShowQuickRevenue(true)} className="flex items-center gap-1 bg-[#006D3B] text-white text-xs py-2 px-3 rounded-lg hover:bg-[#004D40]"><TrendUp size={14} />Quick Revenue</button>
              <button onClick={() => setShowAddJE(true)} className="flex items-center gap-1 bg-[#7C4DFF] text-white text-xs py-2 px-3 rounded-lg hover:bg-[#651FFF]"><Plus size={14} weight="bold" />New Entry</button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b border-gray-100"><th className="text-left py-2.5 px-3 text-gray-500">Date</th><th className="text-left py-2.5 px-3 text-gray-500">Entry #</th><th className="text-left py-2.5 px-3 text-gray-500">Description</th><th className="text-left py-2.5 px-3 text-gray-500">Category</th><th className="text-right py-2.5 px-3 text-gray-500">Debit</th><th className="text-right py-2.5 px-3 text-gray-500">Credit</th><th className="text-center py-2.5 px-3 text-gray-500">Status</th><th className="text-center py-2.5 px-3 text-gray-500">Actions</th></tr></thead>
              <tbody>
                {journalEntries.map((e, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-purple-50/30">
                    <td className="py-2 px-3">{fmtDate(e.date)}</td>
                    <td className="py-2 px-3 font-mono text-[#7C4DFF] font-medium">{e.entry_number}</td>
                    <td className="py-2 px-3 font-medium text-gray-800">{e.description}</td>
                    <td className="py-2 px-3"><span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{e.category}</span></td>
                    <td className="py-2 px-3 text-right font-medium text-blue-700">{fmtD(e.total_debit)}</td>
                    <td className="py-2 px-3 text-right font-medium text-green-700">{fmtD(e.total_credit)}</td>
                    <td className="py-2 px-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${e.status === 'Posted' ? 'bg-green-100 text-green-700' : e.status === 'Voided' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{e.status}</span></td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setJeDetail(e)} className="text-[#1565C0] hover:bg-blue-50 p-1 rounded" title="View"><Eye size={14} /></button>
                        {e.status === 'Draft' && <button onClick={() => handlePostJE(e.entry_number)} className="text-[#2E7D32] hover:bg-green-50 p-1 rounded" title="Post"><Check size={14} weight="bold" /></button>}
                        {e.status === 'Posted' && <button onClick={() => handleVoidJE(e.entry_number)} className="text-[#C62828] hover:bg-red-50 p-1 rounded" title="Void"><Trash size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {journalEntries.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No journal entries yet. Create your first entry!</div>}
          </div>
        </TabsContent>

        {/* ===== INCOME STATEMENT ===== */}
        <TabsContent value="income-statement" className="space-y-4 mt-4">
          {incomeStatement ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-800 text-center mb-1">MyanmarLink Co., Ltd.</h2>
              <h3 className="text-sm text-gray-500 text-center mb-4">Income Statement (Profit & Loss) - {incomeStatement.period}</h3>
              <div className="border-t-2 border-gray-800 pt-3">
                <h4 className="text-sm font-bold text-[#006D3B] mb-2 flex items-center gap-1"><TrendUp size={14} />REVENUE</h4>
                {incomeStatement.revenue.accounts.filter(a => a.balance > 0).map((a, i) => (
                  <div key={i} className="flex justify-between py-1 px-4 text-xs hover:bg-green-50/50">
                    <span className="text-gray-700">{a.code} - {a.name}</span>
                    <span className="font-medium text-green-700">{fmtD(a.balance)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 px-4 text-xs font-bold border-t border-green-200 mt-2 text-[#006D3B]">
                  <span>Total Revenue</span><span>{fmtD(incomeStatement.revenue.total)}</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-3 mt-4">
                <h4 className="text-sm font-bold text-[#E65100] mb-2 flex items-center gap-1"><TrendDown size={14} />EXPENSES</h4>
                {incomeStatement.expenses.accounts.filter(a => a.balance > 0).map((a, i) => (
                  <div key={i} className="flex justify-between py-1 px-4 text-xs hover:bg-orange-50/50">
                    <span className="text-gray-700">{a.code} - {a.name}</span>
                    <span className="font-medium text-orange-700">{fmtD(a.balance)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 px-4 text-xs font-bold border-t border-orange-200 mt-2 text-[#E65100]">
                  <span>Total Expenses</span><span>{fmtD(incomeStatement.expenses.total)}</span>
                </div>
              </div>
              <div className={`flex justify-between py-3 px-4 text-sm font-bold border-t-2 border-b-2 border-gray-800 mt-4 ${incomeStatement.net_income >= 0 ? 'text-[#006D3B] bg-green-50' : 'text-[#C62828] bg-red-50'} rounded-lg`}>
                <span>NET {incomeStatement.net_income >= 0 ? 'INCOME' : 'LOSS'}</span><span>{fmtD(incomeStatement.net_income)}</span>
              </div>
            </div>
          ) : <div className="text-center py-12 text-gray-400">Loading income statement...</div>}
        </TabsContent>

        {/* ===== BALANCE SHEET ===== */}
        <TabsContent value="balance-sheet" className="space-y-4 mt-4">
          {balanceSheet ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-800 text-center mb-1">MyanmarLink Co., Ltd.</h2>
              <h3 className="text-sm text-gray-500 text-center mb-4">Balance Sheet (Statement of Financial Position)</h3>
              <div className={`text-center text-xs mb-4 py-1.5 rounded-lg ${balanceSheet.is_balanced ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {balanceSheet.is_balanced ? 'Balanced: Assets = Liabilities + Equity' : 'NOT BALANCED - Please review entries'}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Assets */}
                <div>
                  <h4 className="text-sm font-bold text-[#1565C0] mb-2 border-b-2 border-[#1565C0] pb-1">ASSETS</h4>
                  {Object.entries(balanceSheet.assets.by_subtype || {}).map(([subtype, data]) => (
                    <div key={subtype} className="mb-3">
                      <div className="text-[11px] font-semibold text-gray-600 mb-1">{subtype}</div>
                      {data.accounts.filter(a => a.balance !== 0).map((a, i) => (
                        <div key={i} className="flex justify-between py-0.5 px-3 text-xs hover:bg-blue-50/50">
                          <span className="text-gray-600">{a.code} - {a.name}</span>
                          <span className="font-medium">{fmtD(a.balance)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between py-1 px-3 text-[11px] font-semibold text-gray-500 border-t border-gray-100">
                        <span>Subtotal</span><span>{fmtD(data.total)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 px-3 text-xs font-bold border-t-2 border-[#1565C0] text-[#1565C0] mt-2">
                    <span>TOTAL ASSETS</span><span>{fmtD(balanceSheet.assets.total)}</span>
                  </div>
                </div>
                {/* Right: Liabilities + Equity */}
                <div>
                  <h4 className="text-sm font-bold text-[#C62828] mb-2 border-b-2 border-[#C62828] pb-1">LIABILITIES</h4>
                  {Object.entries(balanceSheet.liabilities.by_subtype || {}).map(([subtype, data]) => (
                    <div key={subtype} className="mb-3">
                      <div className="text-[11px] font-semibold text-gray-600 mb-1">{subtype}</div>
                      {data.accounts.filter(a => a.balance !== 0).map((a, i) => (
                        <div key={i} className="flex justify-between py-0.5 px-3 text-xs hover:bg-red-50/50">
                          <span className="text-gray-600">{a.code} - {a.name}</span>
                          <span className="font-medium">{fmtD(a.balance)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="flex justify-between py-2 px-3 text-xs font-bold border-t border-[#C62828] text-[#C62828]">
                    <span>Total Liabilities</span><span>{fmtD(balanceSheet.liabilities.total)}</span>
                  </div>
                  <h4 className="text-sm font-bold text-[#2E7D32] mb-2 border-b-2 border-[#2E7D32] pb-1 mt-4">EQUITY</h4>
                  {Object.entries(balanceSheet.equity.by_subtype || {}).map(([subtype, data]) => (
                    <div key={subtype} className="mb-2">
                      {data.accounts.filter(a => a.balance !== 0).map((a, i) => (
                        <div key={i} className="flex justify-between py-0.5 px-3 text-xs hover:bg-green-50/50">
                          <span className="text-gray-600">{a.code} - {a.name}</span>
                          <span className="font-medium">{fmtD(a.balance)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {balanceSheet.equity.net_income !== 0 && (
                    <div className="flex justify-between py-0.5 px-3 text-xs hover:bg-green-50/50">
                      <span className="text-gray-600 italic">Net Income (Current Period)</span>
                      <span className="font-medium">{fmtD(balanceSheet.equity.net_income)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 px-3 text-xs font-bold border-t border-[#2E7D32] text-[#2E7D32]">
                    <span>Total Equity</span><span>{fmtD(balanceSheet.equity.total)}</span>
                  </div>
                  <div className="flex justify-between py-2 px-3 text-xs font-bold border-t-2 border-gray-800 mt-2">
                    <span>TOTAL LIABILITIES + EQUITY</span><span>{fmtD(balanceSheet.liabilities.total + balanceSheet.equity.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : <div className="text-center py-12 text-gray-400">Loading balance sheet...</div>}
        </TabsContent>

        {/* ===== CASH FLOW ===== */}
        <TabsContent value="cash-flow" className="space-y-4 mt-4">
          {cashFlow ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-800 text-center mb-1">MyanmarLink Co., Ltd.</h2>
              <h3 className="text-sm text-gray-500 text-center mb-4">Cash Flow Statement - {cashFlow.period}</h3>
              {/* Operating */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-[#1565C0] mb-2 border-b border-[#1565C0] pb-1">Operating Activities</h4>
                {cashFlow.operating.items.length > 0 ? cashFlow.operating.items.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 px-4 text-xs hover:bg-blue-50/50">
                    <span className="text-gray-600">{fmtDate(item.date)} - {item.description}</span>
                    <span className={`font-medium ${item.amount >= 0 ? 'text-green-700' : 'text-red-600'}`}>{item.amount >= 0 ? '+' : ''}{fmtD(item.amount)}</span>
                  </div>
                )) : <div className="text-xs text-gray-400 px-4 py-2">No operating cash flows</div>}
                <div className="flex justify-between py-2 px-4 text-xs font-bold border-t border-[#1565C0] text-[#1565C0]">
                  <span>Net Operating Cash Flow</span><span>{cashFlow.operating.total >= 0 ? '+' : ''}{fmtD(cashFlow.operating.total)}</span>
                </div>
              </div>
              {/* Investing */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-[#E65100] mb-2 border-b border-[#E65100] pb-1">Investing Activities</h4>
                {cashFlow.investing.items.length > 0 ? cashFlow.investing.items.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 px-4 text-xs hover:bg-orange-50/50">
                    <span className="text-gray-600">{fmtDate(item.date)} - {item.description}</span>
                    <span className={`font-medium ${item.amount >= 0 ? 'text-green-700' : 'text-red-600'}`}>{item.amount >= 0 ? '+' : ''}{fmtD(item.amount)}</span>
                  </div>
                )) : <div className="text-xs text-gray-400 px-4 py-2">No investing cash flows</div>}
                <div className="flex justify-between py-2 px-4 text-xs font-bold border-t border-[#E65100] text-[#E65100]">
                  <span>Net Investing Cash Flow</span><span>{cashFlow.investing.total >= 0 ? '+' : ''}{fmtD(cashFlow.investing.total)}</span>
                </div>
              </div>
              {/* Financing */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-[#2E7D32] mb-2 border-b border-[#2E7D32] pb-1">Financing Activities</h4>
                {cashFlow.financing.items.length > 0 ? cashFlow.financing.items.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 px-4 text-xs hover:bg-green-50/50">
                    <span className="text-gray-600">{fmtDate(item.date)} - {item.description}</span>
                    <span className={`font-medium ${item.amount >= 0 ? 'text-green-700' : 'text-red-600'}`}>{item.amount >= 0 ? '+' : ''}{fmtD(item.amount)}</span>
                  </div>
                )) : <div className="text-xs text-gray-400 px-4 py-2">No financing cash flows</div>}
                <div className="flex justify-between py-2 px-4 text-xs font-bold border-t border-[#2E7D32] text-[#2E7D32]">
                  <span>Net Financing Cash Flow</span><span>{cashFlow.financing.total >= 0 ? '+' : ''}{fmtD(cashFlow.financing.total)}</span>
                </div>
              </div>
              {/* Summary */}
              <div className="border-t-2 border-gray-800 pt-3 space-y-2">
                <div className="flex justify-between px-4 text-xs font-bold">
                  <span>Net Change in Cash</span><span className={cashFlow.net_change >= 0 ? 'text-green-700' : 'text-red-600'}>{cashFlow.net_change >= 0 ? '+' : ''}{fmtD(cashFlow.net_change)}</span>
                </div>
                <div className="flex justify-between px-4 text-sm font-bold bg-blue-50 py-2 rounded-lg text-[#1565C0]">
                  <span>Cash Balance</span><span>{fmtD(cashFlow.cash_balance)}</span>
                </div>
              </div>
            </div>
          ) : <div className="text-center py-12 text-gray-400">Loading cash flow statement...</div>}
        </TabsContent>

        {/* ===== TRIAL BALANCE ===== */}
        <TabsContent value="trial-balance" className="space-y-4 mt-4">
          {trialBalance ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-800 text-center mb-1">MyanmarLink Co., Ltd.</h2>
              <h3 className="text-sm text-gray-500 text-center mb-4">Trial Balance</h3>
              <table className="w-full text-xs">
                <thead><tr className="border-b-2 border-gray-800"><th className="text-left py-2 text-gray-600">Code</th><th className="text-left py-2 text-gray-600">Account Name</th><th className="text-left py-2 text-gray-600">Type</th><th className="text-right py-2 text-gray-600">Debit</th><th className="text-right py-2 text-gray-600">Credit</th></tr></thead>
                <tbody>
                  {trialBalance.accounts.map((a, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 font-mono">{a.code}</td>
                      <td className="py-1.5 font-medium text-gray-800">{a.name}</td>
                      <td className="py-1.5"><span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: TYPE_COLORS[a.type] }}>{a.type}</span></td>
                      <td className="py-1.5 text-right font-medium text-blue-700">{a.debit > 0 ? fmtD(a.debit) : ''}</td>
                      <td className="py-1.5 text-right font-medium text-green-700">{a.credit > 0 ? fmtD(a.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-800 font-bold">
                    <td colSpan={3} className="py-2">TOTALS</td>
                    <td className="py-2 text-right text-blue-700">{fmtD(trialBalance.total_debit)}</td>
                    <td className="py-2 text-right text-green-700">{fmtD(trialBalance.total_credit)}</td>
                  </tr>
                </tfoot>
              </table>
              <div className={`mt-3 text-center text-xs py-1.5 rounded-lg ${Math.abs(trialBalance.total_debit - trialBalance.total_credit) < 0.01 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {Math.abs(trialBalance.total_debit - trialBalance.total_credit) < 0.01 ? 'Trial Balance is in balance' : `Difference: ${fmtD(Math.abs(trialBalance.total_debit - trialBalance.total_credit))}`}
              </div>
            </div>
          ) : <div className="text-center py-12 text-gray-400">Loading trial balance...</div>}
        </TabsContent>

        {/* ===== GENERAL LEDGER (hidden tab) ===== */}
        {tab === 'ledger' && ledgerData && (
          <div className="space-y-4 mt-4">
            <button onClick={() => { setTab('accounts'); setLedgerData(null); }} className="flex items-center gap-1 text-xs text-[#1565C0] hover:underline"><ArrowLeft size={14} />Back to Chart of Accounts</button>
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-1">General Ledger: {ledgerData.account.code} - {ledgerData.account.name}</h3>
              <div className="flex gap-3 text-[11px] text-gray-500 mb-3">
                <span>Type: <span className="font-medium" style={{ color: TYPE_COLORS[ledgerData.account.type] }}>{ledgerData.account.type}</span></span>
                <span>Ending Balance: <span className="font-bold">{fmtD(ledgerData.ending_balance)}</span></span>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-gray-200"><th className="text-left py-2">Date</th><th className="text-left py-2">Entry #</th><th className="text-left py-2">Description</th><th className="text-right py-2">Debit</th><th className="text-right py-2">Credit</th><th className="text-right py-2">Balance</th></tr></thead>
                <tbody>
                  {ledgerData.lines.map((l, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5">{fmtDate(l.date)}</td>
                      <td className="py-1.5 font-mono text-[#7C4DFF]">{l.entry_number}</td>
                      <td className="py-1.5">{l.description}</td>
                      <td className="py-1.5 text-right text-blue-700">{l.debit > 0 ? fmtD(l.debit) : ''}</td>
                      <td className="py-1.5 text-right text-green-700">{l.credit > 0 ? fmtD(l.credit) : ''}</td>
                      <td className="py-1.5 text-right font-medium">{fmtD(l.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ledgerData.lines.length === 0 && <div className="text-center py-6 text-gray-400 text-sm">No transactions for this account</div>}
            </div>
          </div>
        )}
      
        <TabsContent value="cash-book">
          <CashBookModule />
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}

      {/* Add Account Dialog */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddAccount(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><Plus size={16} className="text-[#1565C0]" />Add New Account</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-[10px] text-gray-500 font-medium">Account Code *</label><input value={accountForm.code} onChange={e => setAccountForm({ ...accountForm, code: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#1565C0] focus:border-transparent" placeholder="5xxx" /></div>
                <div className="col-span-2"><label className="text-[10px] text-gray-500 font-medium">Account Name *</label><input value={accountForm.name} onChange={e => setAccountForm({ ...accountForm, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#1565C0] focus:border-transparent" placeholder="Account name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-gray-500 font-medium">Type *</label>
                  <select value={accountForm.type} onChange={e => setAccountForm({ ...accountForm, type: e.target.value, subtype: '' })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                    {['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="text-[10px] text-gray-500 font-medium">Subtype</label>
                  <select value={accountForm.subtype} onChange={e => setAccountForm({ ...accountForm, subtype: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                    <option value="">Select...</option>
                    {(accountTypes.subtypes[accountForm.type] || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Description</label><input value={accountForm.description} onChange={e => setAccountForm({ ...accountForm, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="Optional description" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddAccount(false)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateAccount} className="text-xs px-4 py-2 rounded-lg bg-[#1565C0] text-white hover:bg-[#0D47A1]">Create Account</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Dialog */}
      {editAccount && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditAccount(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-4">Edit Account: {editAccount.code}</h3>
            <div className="space-y-3">
              <div><label className="text-[10px] text-gray-500 font-medium">Account Name</label><input value={editAccount.name} onChange={e => setEditAccount({ ...editAccount, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" /></div>
              <div><label className="text-[10px] text-gray-500 font-medium">Subtype</label>
                <select value={editAccount.subtype} onChange={e => setEditAccount({ ...editAccount, subtype: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  <option value="">Select...</option>
                  {(accountTypes.subtypes[editAccount.type] || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Description</label><input value={editAccount.description || ''} onChange={e => setEditAccount({ ...editAccount, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={editAccount.is_active} onChange={e => setEditAccount({ ...editAccount, is_active: e.target.checked })} /><span className="text-xs">Active</span></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditAccount(null)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleUpdateAccount} className="text-xs px-4 py-2 rounded-lg bg-[#F57F17] text-white hover:bg-[#F9A825]">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* New Journal Entry Dialog */}
      {showAddJE && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddJE(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><Scroll size={16} className="text-[#7C4DFF]" />New Journal Entry</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <div><label className="text-[10px] text-gray-500 font-medium">Date *</label><input type="date" value={jeForm.date} onChange={e => setJeForm({ ...jeForm, date: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" /></div>
                <div className="col-span-2"><label className="text-[10px] text-gray-500 font-medium">Description *</label><input value={jeForm.description} onChange={e => setJeForm({ ...jeForm, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="Transaction description" /></div>
                <div><label className="text-[10px] text-gray-500 font-medium">Reference</label><input value={jeForm.reference} onChange={e => setJeForm({ ...jeForm, reference: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="INV-001" /></div>
              </div>
              {/* Lines */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] text-gray-500 font-medium">Entry Lines (Debit = Credit)</label>
                  <button onClick={addJELine} className="text-[10px] text-[#7C4DFF] hover:underline flex items-center gap-0.5"><Plus size={12} />Add Line</button>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-200"><th className="text-left py-1.5 text-gray-500">Account</th><th className="text-right py-1.5 text-gray-500 w-28">Debit</th><th className="text-right py-1.5 text-gray-500 w-28">Credit</th><th className="text-left py-1.5 text-gray-500">Memo</th><th className="w-8"></th></tr></thead>
                  <tbody>
                    {jeForm.lines.map((line, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-1">
                          <select value={line.account_code} onChange={e => updateJELine(i, 'account_code', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs">
                            <option value="">Select account...</option>
                            {['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'].map(type => (
                              <optgroup key={type} label={type}>
                                {accounts.filter(a => a.type === type && a.is_active).map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        <td className="py-1"><input type="number" value={line.debit} onChange={e => updateJELine(i, 'debit', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-right" placeholder="0.00" /></td>
                        <td className="py-1"><input type="number" value={line.credit} onChange={e => updateJELine(i, 'credit', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-right" placeholder="0.00" /></td>
                        <td className="py-1"><input value={line.memo} onChange={e => updateJELine(i, 'memo', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" placeholder="Memo" /></td>
                        <td className="py-1 text-center"><button onClick={() => removeJELine(i)} className="text-red-400 hover:text-red-600"><Trash size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 font-bold">
                      <td className="py-2 text-right">Totals:</td>
                      <td className="py-2 text-right text-blue-700">{fmtD(jeDebitTotal)}</td>
                      <td className="py-2 text-right text-green-700">{fmtD(jeCreditTotal)}</td>
                      <td colSpan={2} className="py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${jeBalanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{jeBalanced ? 'Balanced' : `Diff: ${fmtD(Math.abs(jeDebitTotal - jeCreditTotal))}`}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddJE(false)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateJE} disabled={!jeBalanced || !jeForm.description || !jeForm.date} className="text-xs px-4 py-2 rounded-lg bg-[#7C4DFF] text-white hover:bg-[#651FFF] disabled:opacity-50 disabled:cursor-not-allowed">Create Entry</button>
            </div>
          </div>
        </div>
      )}

      {/* Journal Entry Detail Dialog */}
      {jeDetail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setJeDetail(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-3">Journal Entry: {jeDetail.entry_number}</h3>
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Date:</span> <span className="font-medium">{fmtDate(jeDetail.date)}</span></div>
                <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${jeDetail.status === 'Posted' ? 'bg-green-100 text-green-700' : jeDetail.status === 'Voided' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{jeDetail.status}</span></div>
              </div>
              <div><span className="text-gray-500">Description:</span> <span className="font-medium">{jeDetail.description}</span></div>
              {jeDetail.reference && <div><span className="text-gray-500">Reference:</span> <span className="font-medium">{jeDetail.reference}</span></div>}
              <div><span className="text-gray-500">Created by:</span> <span className="font-medium">{jeDetail.created_by}</span> on {fmtDate(jeDetail.created_at)}</div>
              {jeDetail.posted_by && <div><span className="text-gray-500">Posted by:</span> <span className="font-medium">{jeDetail.posted_by}</span> on {fmtDate(jeDetail.posted_at)}</div>}
              <table className="w-full text-xs mt-3">
                <thead><tr className="border-b border-gray-200"><th className="text-left py-1.5">Account</th><th className="text-right py-1.5">Debit</th><th className="text-right py-1.5">Credit</th><th className="text-left py-1.5">Memo</th></tr></thead>
                <tbody>
                  {jeDetail.lines?.map((l, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-1.5">{l.account_code} - {l.account_name || accounts.find(a => a.code === l.account_code)?.name || ''}</td>
                      <td className="py-1.5 text-right text-blue-700">{l.debit > 0 ? fmtD(l.debit) : ''}</td>
                      <td className="py-1.5 text-right text-green-700">{l.credit > 0 ? fmtD(l.credit) : ''}</td>
                      <td className="py-1.5 text-gray-500">{l.memo}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 border-gray-300 font-bold"><td>Total</td><td className="text-right text-blue-700">{fmtD(jeDetail.total_debit)}</td><td className="text-right text-green-700">{fmtD(jeDetail.total_credit)}</td><td></td></tr></tfoot>
              </table>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setJeDetail(null)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Expense Dialog */}
      {showQuickExpense && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowQuickExpense(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-[#E65100] mb-4 flex items-center gap-2"><TrendDown size={16} />Record Expense</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-gray-500 font-medium">Date *</label><input type="date" value={quickForm.date} onChange={e => setQuickForm({ ...quickForm, date: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" /></div>
                <div><label className="text-[10px] text-gray-500 font-medium">Amount *</label><input type="number" value={quickForm.amount} onChange={e => setQuickForm({ ...quickForm, amount: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="0" /></div>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Description *</label><input value={quickForm.description} onChange={e => setQuickForm({ ...quickForm, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="What was this expense for?" /></div>
              <div><label className="text-[10px] text-gray-500 font-medium">Expense Category *</label>
                <select value={quickForm.expense_account} onChange={e => setQuickForm({ ...quickForm, expense_account: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  <option value="">Select expense account...</option>
                  {expenseAccounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Paid From *</label>
                <select value={quickForm.payment_account} onChange={e => setQuickForm({ ...quickForm, payment_account: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  {cashAccounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Reference</label><input value={quickForm.reference} onChange={e => setQuickForm({ ...quickForm, reference: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="Receipt #, Invoice #" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={quickForm.auto_post} onChange={e => setQuickForm({ ...quickForm, auto_post: e.target.checked })} /><span className="text-xs">Auto-post (immediately update balances)</span></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowQuickExpense(false)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleQuickExpense} className="text-xs px-4 py-2 rounded-lg bg-[#E65100] text-white hover:bg-[#BF360C]">Record Expense</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Revenue Dialog */}
      {showQuickRevenue && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowQuickRevenue(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-[#006D3B] mb-4 flex items-center gap-2"><TrendUp size={16} />Record Revenue</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-gray-500 font-medium">Date *</label><input type="date" value={quickForm.date} onChange={e => setQuickForm({ ...quickForm, date: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" /></div>
                <div><label className="text-[10px] text-gray-500 font-medium">Amount *</label><input type="number" value={quickForm.amount} onChange={e => setQuickForm({ ...quickForm, amount: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="0" /></div>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Description *</label><input value={quickForm.description} onChange={e => setQuickForm({ ...quickForm, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="Revenue source description" /></div>
              <div><label className="text-[10px] text-gray-500 font-medium">Revenue Category *</label>
                <select value={quickForm.revenue_account} onChange={e => setQuickForm({ ...quickForm, revenue_account: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  <option value="">Select revenue account...</option>
                  {revenueAccounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Deposit To *</label>
                <select value={quickForm.deposit_account} onChange={e => setQuickForm({ ...quickForm, deposit_account: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs">
                  {cashAccounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-gray-500 font-medium">Reference</label><input value={quickForm.reference} onChange={e => setQuickForm({ ...quickForm, reference: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs" placeholder="Invoice #" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={quickForm.auto_post} onChange={e => setQuickForm({ ...quickForm, auto_post: e.target.checked })} /><span className="text-xs">Auto-post (immediately update balances)</span></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowQuickRevenue(false)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleQuickRevenue} className="text-xs px-4 py-2 rounded-lg bg-[#006D3B] text-white hover:bg-[#004D40]">Record Revenue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ACCOUNT_TYPES_LIST = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
